import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useGetAdminTicketQuery,
  useReplyToAdminTicketMutation,
  useResolveTicketMutation,
  useCloseTicketMutation,
  buildTicketReplyFormData,
} from '../../features/tickets/ticketsApi';
import { TICKET_CATEGORIES, ticketReplySchema } from '../../lib/validators/ticketSchema';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import TicketStatusBadge from '../../components/tickets/TicketStatusBadge';
import TicketThread from '../../components/tickets/TicketThread';
import TicketAttachmentsInput from '../../components/tickets/TicketAttachmentsInput';
import { DataRow } from '../../components/ui/MobileCard';
import { SkeletonDetailCard } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';

const CATEGORY_LABEL = Object.fromEntries(TICKET_CATEGORIES.map((c) => [c.value, c.label]));

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * GET /admin/support-tickets/{uuid} — permission: manage-support, any
 * investor's ticket. Resolve only shows while open; Close shows while
 * open OR resolved (either can go straight to closed). Reply is disabled
 * entirely once closed — same terminal-state treatment as the investor
 * side, just without a Reopen action (that's investor-only per the spec).
 */
export default function TicketDetail() {
  const { id } = useParams();
  const toast = useToast();
  const { data: ticket, isLoading, isError, error } = useGetAdminTicketQuery(id, { skip: !id });
  const [replyToTicket, { isLoading: isReplying }] = useReplyToAdminTicketMutation();
  const [resolveTicket, { isLoading: isResolving }] = useResolveTicketMutation();
  const [closeTicket, { isLoading: isClosing }] = useCloseTicketMutation();

  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [replyError, setReplyError] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const isOpen = ticket?.status === 'open';
  const isResolved = ticket?.status === 'resolved';
  const isClosed = ticket?.status === 'closed';

  const onReply = async (e) => {
    e.preventDefault();
    setReplyError(null);

    const parsed = ticketReplySchema.safeParse({ body, attachments });
    if (!parsed.success) {
      setReplyError(parsed.error.issues[0]?.message || 'Please check your reply.');
      return;
    }

    const wasResolved = isResolved;
    try {
      const formData = buildTicketReplyFormData({ body, attachments });
      await replyToTicket({ id, formData }).unwrap();
      setBody('');
      setAttachments([]);
      toast.push({
        tone: 'success',
        message: wasResolved ? 'Reply sent — ticket reopened.' : 'Reply sent.',
      });
    } catch (err) {
      if (err?.status === 409) {
        setReplyError('This ticket is closed and can no longer receive replies.');
        return;
      }
      setReplyError(authErrorMessage(err, { fallback: 'Could not send this reply.' }));
    }
  };

  const onResolve = async () => {
    try {
      await resolveTicket(id).unwrap();
      toast.push({ tone: 'success', message: 'Ticket marked resolved — investor notified by email.' });
      setResolveOpen(false);
    } catch (err) {
      toast.push({ tone: 'danger', message: authErrorMessage(err, { fallback: 'Could not resolve this ticket.' }) });
      setResolveOpen(false);
    }
  };

  const onCloseTicket = async () => {
    try {
      await closeTicket(id).unwrap();
      toast.push({ tone: 'success', message: 'Ticket closed.' });
      setCloseOpen(false);
    } catch (err) {
      toast.push({ tone: 'danger', message: authErrorMessage(err, { fallback: 'Could not close this ticket.' }) });
      setCloseOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/tickets" className="text-sm text-accent hover:underline">
        ← Back to queue
      </Link>

      {isLoading && <SkeletonDetailCard fields={4} />}

      {isError && (
        <Card>
          <p className="py-6 text-center text-danger">
            {authErrorMessage(error, {
              notFound: 'Ticket not found.',
              forbidden: "Your admin account doesn't have the manage-support permission needed to view this ticket.",
            })}
          </p>
        </Card>
      )}

      {ticket && (
        <>
          <Card className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{ticket.subject}</h1>
                <p className="text-sm text-foreground-muted mt-0.5">
                  {CATEGORY_LABEL[ticket.category] || ticket.category} · Opened {formatDate(ticket.createdAt)}
                </p>
              </div>
              <TicketStatusBadge status={ticket.status} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-border">
              <DataRow label="Investor" value={ticket.investor?.fullName || '—'} />
              <DataRow label="Email" value={ticket.investor?.email || '—'} />
              {ticket.resolvedAt && <DataRow label="Resolved" value={`${formatDate(ticket.resolvedAt)} by ${ticket.resolver?.fullName || '—'}`} />}
              {ticket.closedAt && <DataRow label="Closed" value={`${formatDate(ticket.closedAt)} by ${ticket.closer?.fullName || '—'}`} />}
            </div>

            {isClosed && (
              <div className="rounded-xl border border-border bg-background-elevated p-3 text-sm text-foreground-muted">
                This ticket is closed and fully locked — no more replies from either side.
              </div>
            )}

            {(isOpen || isResolved) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                {isOpen && (
                  <Button size="sm" onClick={() => setResolveOpen(true)}>
                    Resolve
                  </Button>
                )}
                <Button size="sm" variant="danger" onClick={() => setCloseOpen(true)}>
                  Close
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Conversation" />
            <TicketThread messages={ticket.messages} />
          </Card>

          {!isClosed && (
            <Card>
              <CardHeader title="Reply" />
              <form onSubmit={onReply} className="space-y-4">
                <textarea
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your reply…"
                  className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
                />
                <TicketAttachmentsInput value={attachments} onChange={setAttachments} />
                {replyError && <p className="text-sm text-danger">{replyError}</p>}
                <Button type="submit" isLoading={isReplying}>
                  Send reply
                </Button>
              </form>
            </Card>
          )}
        </>
      )}

      <ConfirmDialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onConfirm={onResolve}
        title="Resolve this ticket?"
        message="The investor will get an email letting them know it's resolved. They can still reply to reopen it, or reopen it manually."
        confirmLabel="Resolve"
        tone="primary"
        isLoading={isResolving}
      />

      <ConfirmDialog
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        onConfirm={onCloseTicket}
        title="Close this ticket?"
        message="This is terminal — no more replies or reopening from either side, and no email is sent. Make sure the conversation is really done."
        confirmLabel="Close ticket"
        tone="danger"
        isLoading={isClosing}
      />
    </div>
  );
}
